import { Navigate, Outlet } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import { FORM_STATUS_ENUM } from "@interfaces/response";
import { isFormStatusEditable } from "@helpers/funciones";
import { PATHS_EXPEDIENTE_V2 } from "@helpers/constantes";

/** Campos de `application` que traen el avance de un formulario del expediente. */
export type CampoFormStatus = "loan_application_information_status" | "references_status" | "bank_information_status";

/** Hub del expediente: es de donde salen los CTAs de estas pantallas. */
const PATH_EXPEDIENTE = PATHS_EXPEDIENTE_V2.empresa.path;

interface Props {
  /** Cuál de los avances de `state.register.application` decide el acceso. */
  attribute: CampoFormStatus;
  allowedStatus?: NonNullable<FORM_STATUS_ENUM>[];
}

/**
 * Deja pasar a una pantalla del expediente solo si su formulario está en uno de los estados
 * permitidos; si no, devuelve al hub. Sirve para que un formulario ya resuelto no se pueda volver a
 * abrir por URL.
 *
 * Un status `null` cuenta como `PENDIENTE`: el backend no lo llena hasta que hay algo capturado, y
 * si se bloqueara, una solicitud recién creada no podría entrar nunca a su propio formulario. Es el
 * mismo criterio de `ButtonExpedienteStatus` en el expediente legacy.
 *
 * Es el hermano de `ProtectRoutesByStatusList`, que guarda por el status del préstamo
 * (`state.register.loan_status`) en vez de por el avance de un formulario.
 */
const ProtectRouteByFormStatus = ({ attribute, allowedStatus }: Props) => {
  const application = useSelector((state: RootState) => state.register.application);
  const status = application[attribute];

  // Sin `allowedStatus` explícito manda la regla compartida con las tarjetas del hub.
  const puedeEntrar = allowedStatus ? allowedStatus.includes(status ?? "PENDIENTE") : isFormStatusEditable(status);

  if (puedeEntrar) {
    return <Outlet />;
  }

  return <Navigate to={PATH_EXPEDIENTE} replace />;
};

export default ProtectRouteByFormStatus;
